import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useReporte } from '../api/hooks';
import type { AgruparReporte, FilaReporte } from '../api/tipos';
import { BarrasH } from '../componentes/Barras';
import { Cargando, ErrorCaja } from '../componentes/ui';
import { aCSV, descargarTexto } from '../lib/csv';
import { aFechaInput, instanteDesdeZona, partesEnZona, sumarDias } from '../lib/fechas';
import { minutos, numero, pesos, signoPct } from '../lib/formato';

const AGRUPAR: { v: AgruparReporte; t: string }[] = [
  { v: 'material', t: 'Material' }, { v: 'maquina', t: 'Máquina' }, { v: 'cliente', t: 'Cliente' }, { v: 'dia', t: 'Día' }, { v: 'estado', t: 'Estado' },
];

function rangos(ahora = Date.now()) {
  const p = partesEnZona(ahora);
  const inicioMes = instanteDesdeZona(p.anio, p.mes, 1);
  const inicioMesPasado = instanteDesdeZona(p.mes === 1 ? p.anio - 1 : p.anio, p.mes === 1 ? 12 : p.mes - 1, 1);
  return [
    { t: '7 días', desde: aFechaInput(sumarDias(ahora, -6)), hasta: aFechaInput(ahora) },
    { t: '30 días', desde: aFechaInput(sumarDias(ahora, -29)), hasta: aFechaInput(ahora) },
    { t: 'Este mes', desde: aFechaInput(inicioMes), hasta: aFechaInput(ahora) },
    { t: 'Mes pasado', desde: aFechaInput(inicioMesPasado), hasta: aFechaInput(inicioMes - 1) },
    { t: '90 días', desde: aFechaInput(sumarDias(ahora, -89)), hasta: aFechaInput(ahora) },
  ];
}

export function csvReporte(filas: FilaReporte[], agrupar: AgruparReporte): string {
  const nombre = AGRUPAR.find((a) => a.v === agrupar)?.t ?? 'Grupo';
  return aCSV(
    [nombre, 'Trabajos', 'Minutos estimados', 'Minutos reales', 'Diferencia real vs estimado (%)', 'Ingresos (MXN)'],
    filas.map((f) => [
      f.etiqueta, f.trabajos, Math.round(f.minutosEstimados), Math.round(f.minutosReales),
      f.minutosEstimados && f.minutosReales ? Math.round(((f.minutosReales - f.minutosEstimados) / f.minutosEstimados) * 100) : '',
      (f.ingresosCentavos / 100).toFixed(2),
    ]),
  );
}

export default function Reportes() {
  const [params, setParams] = useSearchParams();
  const presets = useMemo(() => rangos(), []);
  const desde = params.get('desde') ?? presets[1].desde;
  const hasta = params.get('hasta') ?? presets[1].hasta;
  const agrupar = (AGRUPAR.some((a) => a.v === params.get('agrupar')) ? params.get('agrupar') : 'material') as AgruparReporte;
  const set = (c: Record<string, string>) => setParams({ desde, hasta, agrupar, ...c }, { replace: true });
  const q = useReporte(desde, hasta, agrupar);
  const filas = q.data ?? [];

  const tot = filas.reduce((s, f) => ({
    trabajos: s.trabajos + f.trabajos, est: s.est + f.minutosEstimados, real: s.real + f.minutosReales, ingresos: s.ingresos + f.ingresosCentavos,
  }), { trabajos: 0, est: 0, real: 0, ingresos: 0 });

  // Real vs estimado sólo donde ya hay tiempo registrado (lo que no se ha cortado no cuenta).
  const estComparable = filas.filter((f) => f.minutosReales > 0).reduce((s, f) => s + f.minutosEstimados, 0);
  const desvio = estComparable && tot.real ? (tot.real - estComparable) / estComparable : null;

  const filasMin = filas.map((f) => ({ clave: f.clave, etiqueta: f.etiqueta, valores: [f.minutosEstimados, f.minutosReales] }));
  const filasIng = filas.map((f) => ({ clave: f.clave, etiqueta: f.etiqueta, valores: [f.ingresosCentavos] }));

  return (
    <>
      <div className="titulo">
        <h1>Reportes</h1>
        <div className="grow" />
        <button type="button" className="btn" disabled={!filas.length}
          onClick={() => descargarTexto(`reporte-laser-${agrupar}-${desde}-a-${hasta}.csv`, csvReporte(filas, agrupar))}>Exportar CSV</button>
      </div>

      <div className="filtros">
        <input type="date" value={desde} max={hasta} onChange={(e) => e.target.value && set({ desde: e.target.value })} aria-label="Desde" />
        <input type="date" value={hasta} min={desde} onChange={(e) => e.target.value && set({ hasta: e.target.value })} aria-label="Hasta" />
        <div className="seg" role="group" aria-label="Rango rápido">
          {presets.map((p) => (
            <button key={p.t} type="button" aria-pressed={p.desde === desde && p.hasta === hasta} onClick={() => set({ desde: p.desde, hasta: p.hasta })}>{p.t}</button>
          ))}
        </div>
      </div>
      <div className="filtros">
        <span className="meta">Agrupar por</span>
        <div className="seg" role="group" aria-label="Agrupar por">
          {AGRUPAR.map((a) => <button key={a.v} type="button" aria-pressed={agrupar === a.v} onClick={() => set({ agrupar: a.v })}>{a.t}</button>)}
        </div>
      </div>

      {q.isError && <ErrorCaja error={q.error} reintentar={() => q.refetch()} />}
      {q.isLoading ? <Cargando /> : (
        <>
          <div className="tiles">
            <div className="tile"><b>{tot.trabajos}</b><span>trabajos</span></div>
            <div className="tile"><b>{pesos(tot.ingresos)}</b><span>ingresos cobrados</span></div>
            <div className="tile"><b>{minutos(tot.est)}</b><span>láser estimado</span></div>
            <div className="tile"><b>{minutos(tot.real)}</b><span>láser real</span></div>
            <div className={`tile${desvio != null && Math.abs(desvio) > 0.15 ? ' tile--alerta' : ''}`} title="Sólo grupos con tiempo registrado">
              <b>{desvio != null ? signoPct(desvio) : '—'}</b><span>real vs estimado</span>
            </div>
            <div className="tile"><b>{tot.real ? pesos(tot.ingresos / tot.real) : '—'}</b><span>ingreso por minuto real</span></div>
          </div>

          <div className="rejilla-2" style={{ marginBottom: 14 }}>
            <div className="tarjeta"><BarrasH titulo="Minutos de láser" filas={filasMin} formato={(n) => numero(Math.round(n))}
              series={[{ nombre: 'Estimados', clase: 's1' }, { nombre: 'Reales', clase: 's2' }]} /></div>
            <div className="tarjeta"><BarrasH titulo="Ingresos cobrados (MXN)" filas={filasIng} formato={(n) => pesos(n)}
              series={[{ nombre: 'Ingresos', clase: 's1' }]} /></div>
          </div>

          <div className="tabla-caja">
            <table>
              <thead><tr>
                <th>{AGRUPAR.find((a) => a.v === agrupar)?.t}</th><th className="num">Trabajos</th><th className="num">Min. estimados</th>
                <th className="num">Min. reales</th><th className="num oc2">Real vs est.</th><th className="num">Ingresos</th>
              </tr></thead>
              <tbody>
                {filas.length === 0 && <tr><td colSpan={6} className="vacio">Sin trabajos en este rango.</td></tr>}
                {filas.map((f) => (
                  <tr key={f.clave}>
                    <td>{f.etiqueta}</td>
                    <td className="num">{f.trabajos}</td>
                    <td className="num">{numero(Math.round(f.minutosEstimados))}</td>
                    <td className="num">{f.minutosReales ? numero(Math.round(f.minutosReales)) : '—'}</td>
                    <td className="num oc2">{f.minutosEstimados && f.minutosReales ? signoPct((f.minutosReales - f.minutosEstimados) / f.minutosEstimados) : '—'}</td>
                    <td className="num">{pesos(f.ingresosCentavos)}</td>
                  </tr>
                ))}
                {filas.length > 1 && (
                  <tr style={{ fontWeight: 700 }}>
                    <td>Total</td><td className="num">{tot.trabajos}</td><td className="num">{numero(Math.round(tot.est))}</td>
                    <td className="num">{numero(Math.round(tot.real))}</td><td className="num oc2">{desvio != null ? signoPct(desvio) : '—'}</td>
                    <td className="num">{pesos(tot.ingresos)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="meta" style={{ marginTop: 8 }}>Rango por fecha de entrada del trabajo (hora de CDMX). Ingresos = trabajos ya pagados.</p>
        </>
      )}
    </>
  );
}
